import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Icon, Tabs } from '../../design';
import { useToast } from '../../design/Toast';
import { fmtRelative } from '../../lib/format';
import { type LockerDetail, StatusBadge, TYPE_LABEL } from './common';
import s from './lockers.module.css';
import { HardwareTab } from './tabs/HardwareTab';
import { KioskProfileTab } from './tabs/KioskProfileTab';
import { LocationTab } from './tabs/LocationTab';
import { LogTab } from './tabs/LogTab';
import { OverviewTab } from './tabs/OverviewTab';
import { RulesTab } from './tabs/RulesTab';
import { SlotsTab } from './tabs/SlotsTab';

type Tab = 'general' | 'compartimente' | 'controler' | 'kiosk' | 'locatie' | 'reguli' | 'jurnal';
const TABS: { value: Tab; label: string }[] = [
  { value: 'general', label: 'Vedere generală' },
  { value: 'compartimente', label: 'Compartimente & layout' },
  { value: 'controler', label: 'Controler & plăci' },
  { value: 'kiosk', label: 'Profil kiosk' },
  { value: 'locatie', label: 'Locație & identitate' },
  { value: 'reguli', label: 'Acces & reguli' },
  { value: 'jurnal', label: 'Jurnal echipament' },
];

export function useLocker(id: string) {
  return useQuery({
    queryKey: ['locker', id],
    queryFn: () => unwrap(api.GET('/lockers/{id}', { params: { path: { id } } })),
  });
}

export function LockerDetailPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'general';
  const { has, withStepUp } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const query = useLocker(id);

  const publish = useMutation({
    mutationFn: () =>
      withStepUp((headers) =>
        unwrap(
          api.POST('/lockers/{id}/config/publish', { params: { path: { id }, header: headers } }),
        ),
      ),
    onSuccess: (v) => {
      toast(
        `Configurația v${v.version} a fost publicată; kioskul o aplică la următoarea comandă.`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['locker', id] });
      void qc.invalidateQueries({ queryKey: ['lockers'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  if (query.isLoading) return <p className="muted">Se încarcă…</p>;
  if (query.error || !query.data)
    return <p style={{ color: 'var(--red)' }}>{errorMessage(query.error)}</p>;
  const l: LockerDetail = query.data;
  const unpublished = l.configVersion !== l.deployedVersion;
  const canPublishStatus = l.status === 'online' || l.status === 'degraded';

  return (
    <>
      <div className="flex" style={{ marginBottom: 6 }}>
        <Link to="/lockere" className="flex small">
          <Icon name="chevronLeft" size={14} /> Lockere
        </Link>
      </div>
      <div className={`flex between wrap`} style={{ marginBottom: 16, alignItems: 'flex-start' }}>
        <div>
          <h1>{l.name}</h1>
          <div className="flex wrap muted small" style={{ marginTop: 6 }}>
            <StatusBadge status={l.status} />
            <span className="mono">{l.serialNumber}</span>
            <span>· {TYPE_LABEL[l.type]}</span>
            <span>· {l.location.city}</span>
            <span>· ultimul contact {fmtRelative(l.lastSeenAt)}</span>
          </div>
        </div>
        <div className="flex">
          <span className="small muted">
            configurație <strong>v{l.configVersion}</strong> · instalată{' '}
            <strong>v{l.deployedVersion}</strong>
          </span>
          {has('publish') && (
            <Button
              variant="primary"
              icon="lock"
              busy={publish.isPending}
              disabled={!unpublished || !canPublishStatus}
              title={
                !unpublished
                  ? 'Nu există modificări nepublicate'
                  : !canPublishStatus
                    ? 'Publicarea cere lockerul online sau degradat'
                    : 'Publică cu confirmare TOTP'
              }
              onClick={() => publish.mutate()}
            >
              Publică configurația
            </Button>
          )}
        </div>
      </div>
      {unpublished && (
        <div className={s.banner} role="status">
          <Icon name="alert" size={18} />
          <span>
            Configurație nepublicată: v{l.configVersion} în consolă, v{l.deployedVersion} pe
            echipament.
            {!canPublishStatus && ' Publicarea este blocată cât timp lockerul nu este online.'}
          </span>
        </div>
      )}
      <Tabs value={tab} onChange={(t) => setParams({ tab: t }, { replace: true })} items={TABS} />
      {tab === 'general' && <OverviewTab locker={l} />}
      {tab === 'compartimente' && <SlotsTab locker={l} />}
      {tab === 'controler' && <HardwareTab locker={l} />}
      {tab === 'kiosk' && <KioskProfileTab locker={l} />}
      {tab === 'locatie' && <LocationTab locker={l} />}
      {tab === 'reguli' && <RulesTab locker={l} />}
      {tab === 'jurnal' && <LogTab locker={l} />}
    </>
  );
}
